/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/** One recognised word: text plus its quad in source-image pixels. */
export interface OcrWord {
  text: string;
  /** `[x1,y1,x2,y2,x3,y3,x4,y4]`, clockwise from top-left. */
  box: number[];
}

export interface OcrLine {
  text: string;
  /** `[x1,y1,x2,y2,x3,y3,x4,y4]`, clockwise from top-left. */
  box: number[];
  words: OcrWord[];
}

export interface OcrResult {
  engine: string;
  fileName: string;
  /** Source image size, used to scale the bounding boxes onto the rendered preview. */
  width: number;
  height: number;
  durationMs: number;
  text: string;
  lines: OcrLine[];
}

export interface OcrHealth {
  status: 'starting' | 'ready' | 'error';
  engine?: string | null;
  error?: string | null;
  platform?: string;
}

/** One page of text as the index holds it. */
export interface OcrIndexedPage {
  page: number;
  text: string;
}

/** Every indexed page of one document — what the .md download is built from. */
export interface OcrIndexedDocument {
  nodeId: string;
  name: string | null;
  pages: OcrIndexedPage[];
}

/**
 * Talks to the OCR sidecar (`services/aca-ocr-api`): the engine that reads the
 * text, and the index that makes it findable afterwards. Which files are read,
 * and how a PDF is turned into pages, is decided in {@link OcrPipelineService}.
 *
 * The OCR itself cannot happen in the browser: the engine is `oneocr.dll`, a native
 * Windows binary, so it runs in a small .NET service that this class posts images to.
 * In development the Angular dev-server proxies `/ocr-api` to it — see
 * `app/proxy.conf.js`.
 *
 * The recognised text goes to the sidecar's own `ocr-text` index, not onto the
 * node. It used to be written into `cm:description`, which had two faults: it
 * destroyed whatever description the document already carried, and Alfresco's
 * analyser reads a Thai sentence as one token, so a word from the middle of a
 * phrase was still not findable. The index holds the text per page, analysed for
 * Thai, and remembers where on the page each line sat — none of which fits in a
 * property. Search reads it back through {@link OcrSearchService}, which puts
 * every id past Alfresco first so permission is still the repository's to decide.
 */
@Injectable({ providedIn: 'root' })
export class OcrService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthenticationService);

  private get baseUrl(): string {
    return this.appConfig.get<string>('ocr.baseUrl', '/ocr-api');
  }

  /**
   * State of the OCR engine. The sidecar loads a ~58 MB model in the background at
   * startup, so this reports `starting` for a few seconds (longer under Wine).
   */
  health(): Observable<OcrHealth> {
    return this.http.get<OcrHealth>(`${this.baseUrl}/api/ocr/health`);
  }

  /**
   * Recognises text in an image.
   * @param maxLines Recognition line cap (1-1000). Omit to use the service default.
   */
  recognize(file: File, maxLines?: number): Observable<OcrResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (maxLines) {
      form.append('maxLines', String(maxLines));
    }

    return this.http.post<OcrResult>(`${this.baseUrl}/api/ocr/recognize`, form, { headers: this.credentials() });
  }

  /**
   * Puts one page of recognised text into the search index, under the node it
   * was read from.
   *
   * The line boxes travel with it so a later reader can be shown *where* on the
   * page the words are, rather than only which page. They are sent in the shape
   * the index stores — `t` for the line, `b` for its corners — because the whole
   * `OcrLine` carries per-word boxes too, and that is several times the size for
   * something nothing reads back.
   *
   * Re-sending the same node and page replaces what was there, so running OCR
   * over a document again corrects the index instead of duplicating it.
   */
  indexOcrText(nodeId: string, name: string, result: OcrResult, page = 1): Observable<boolean> {
    const boxes = (result.lines ?? []).map((line) => ({ t: line.text, b: line.box }));
    return this.putPage(nodeId, name, page, result.text, boxes, result.engine);
  }

  /**
   * Puts one page of text that did not need reading — a PDF page that carries
   * its own text layer. It has no line boxes: pdf.js positions text in PDF
   * units, not in the pixels the overlay draws in, and nothing reads them back
   * for these pages.
   */
  indexText(nodeId: string, name: string, page: number, text: string, engine: string): Observable<boolean> {
    return this.putPage(nodeId, name, page, text, [], engine);
  }

  private putPage(nodeId: string, name: string, page: number, text: string, boxes: unknown[], engine: string): Observable<boolean> {
    return this.http
      .post(`${this.baseUrl}/api/ocr/index`, { nodeId, name, page, text, boxes, engine }, { headers: this.credentials() })
      .pipe(map(() => true));
  }

  /**
   * Forgets everything the index holds about a node.
   *
   * Only for a node that is gone for good. A document in the trash needs nothing
   * done here: the index is never trusted on its own — every hit is put to
   * Alfresco before it is shown — and Alfresco does not answer for an archived
   * node, so a trashed document drops out of results by itself and comes back
   * with its text intact if it is restored.
   */
  removeFromIndex(nodeId: string): Observable<boolean> {
    return this.http.delete(`${this.baseUrl}/api/ocr/index/${encodeURIComponent(nodeId)}`, { headers: this.credentials() }).pipe(map(() => true));
  }

  /**
   * Reads back every page the index holds for a node — for the "download text"
   * action on the preview page, which turns the answer into a .md.
   *
   * The sidecar checks the node against Alfresco with the credentials sent here
   * before it answers, so a reader gets the text of a document they may open
   * and a 403/404 otherwise. `pages` is empty when nothing was ever indexed.
   */
  indexedText(nodeId: string): Observable<OcrIndexedDocument> {
    return this.http.get<OcrIndexedDocument>(`${this.baseUrl}/api/ocr/index/${encodeURIComponent(nodeId)}`, {
      headers: this.credentials()
    });
  }

  /**
   * The sidecar asks Alfresco on this reader's behalf — for reading, and for
   * writing or removing text, which it allows only to someone who may edit the
   * document — so every call needs credentials Alfresco accepts. A ticket goes in the `ROLE_TICKET` form, which is what the
   * repository expects when the password itself is not being sent.
   */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}
